import { Box, Flex, Spacer } from '@chakra-ui/react';
import { ReactNode } from 'react';
import VDotMenu from '@voly/ui-components/src/v2/VDotMenu';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { translate } from 'src/app/project/features/translate/translate';
import { isCustomFieldLockable } from 'src/app/project/modules/custom-fields/utils/is-custom-field-lockable';
import { isCustomFieldVisibilityEditable } from 'src/app/project/modules/custom-fields/utils/is-custom-field-visibility-editable';
import { TLayoutField } from '../organisms/CustomFieldGroupsSettings';
import { getCustomFieldTypeLabel } from '../../utils/customFieldType';
import CustomFieldTypeIcon from './custom-fields/CustomFieldTypeIcon';
import { EIntegrationStatus } from 'src/app/project/modules/custom-fields/custom-fields.model';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';

const MUTED_ICON_COLOUR = 'fg.muted';
const ROW_PADDING = { p: 4 };
const GROUPED_ROW_STYLE = { ...ROW_PADDING, borderBottomWidth: '1px' };
const STANDALONE_ROW_STYLE = { ...ROW_PADDING, my: 4, borderWidth: '1px', borderRadius: 'md' };

interface SettingsCustomFieldRowProps {
  field: TLayoutField;
  content: ReactNode;
  dragHandle: ReactNode;
  isGrouped: boolean;
  onDeleteCustomField: (fieldId: string) => void;
  onSetVisibility: (display: boolean) => void;
  onSetLocked: (locked: boolean) => void;
  onEditCustomField: (fieldId: string) => void;
}

const SettingsCustomFieldRow = ({
  field,
  content,
  dragHandle,
  isGrouped,
  onDeleteCustomField,
  onSetVisibility,
  onEditCustomField,
  onSetLocked,
}: SettingsCustomFieldRowProps) => {
  const handleEdit = () => {
    onEditCustomField(field.id);
  };

  const handleDelete = () => {
    onDeleteCustomField(field.id);
  };

  const isVolyIntegrationField =
    field.volyIntegrationActive === EIntegrationStatus.DISABLED ||
    field.volyIntegrationActive === EIntegrationStatus.ON;

  const dotMenuItems = [];

  if (!isVolyIntegrationField && !field.lockedTemplate) {
    dotMenuItems.push({ value: 'edit', label: 'Edit', onClick: handleEdit });
  }

  if (isCustomFieldVisibilityEditable(field)) {
    dotMenuItems.push({
      value: 'visibility',
      label: field.display ? 'Hide' : 'Show',
      onClick: () => onSetVisibility(!field.display),
    });
  }

  if (isCustomFieldLockable(field)) {
    dotMenuItems.push({
      value: 'lock',
      label: field.lockedValue ? 'Unlock' : 'Lock',
      onClick: () => onSetLocked(!field.lockedValue),
    });
  }

  if (!field.lockedTemplate) {
    dotMenuItems.push({ value: 'delete', label: 'Delete', onClick: handleDelete });
  }

  const hasDotMenuItems = dotMenuItems.length > 0;

  return (
    <Flex
      align="center"
      gap={2}
      p={4}
      data-testid="custom-field-row"
      {...(isGrouped ? GROUPED_ROW_STYLE : STANDALONE_ROW_STYLE)}
    >
      <Flex align="center" gap={2} data-testid="custom-field-row-label">
        {dragHandle}
        {content}
      </Flex>
      <Spacer />

      <Flex align="center" gap={12}>
        <Flex w={10} justify="center" color={MUTED_ICON_COLOUR}>
          {isCustomFieldVisibilityEditable(field) ? (
            <VTooltip
              content={translate(
                field.display ? 'custom_field_visible_tooltip' : 'custom_field_hidden_tooltip',
              )}
              contentProps={undefined}
              disabled={undefined}
            >
              <Box
                as="button"
                cursor="pointer"
                color={MUTED_ICON_COLOUR}
                aria-label={field.display ? `Hide ${field.label}` : `Show ${field.label}`}
                onClick={() => onSetVisibility(!field.display)}
              >
                {field.display ? (
                  <VMaterialIcon
                    pack=""
                    size="md"
                    name="visibility"
                    data-testid="field-visibility-icon"
                  />
                ) : (
                  <VMaterialIcon
                    size="md"
                    name="visibilityOff"
                    data-testid="field-visibility-icon"
                    pack=""
                  />
                )}
              </Box>
            </VTooltip>
          ) : null}
        </Flex>

        <Flex w={10} justify="center" color={MUTED_ICON_COLOUR}>
          {isCustomFieldLockable(field) ? (
            <VTooltip
              content={translate(
                field.lockedValue ? 'custom_field_is_locked' : 'custom_field_is_editable',
              )}
              contentProps={undefined}
              disabled={undefined}
            >
              <Box
                as="button"
                cursor="pointer"
                color={MUTED_ICON_COLOUR}
                aria-label={field.lockedValue ? `Unlock ${field.label}` : `Lock ${field.label}`}
                onClick={() => onSetLocked(!field.lockedValue)}
              >
                {field.lockedValue ? (
                  <VMaterialIcon size="md" pack="" name="lock" data-testid="field-lock-icon" />
                ) : (
                  <VMaterialIcon size="md" pack="" name="lockOpen" data-testid="field-lock-icon" />
                )}
              </Box>
            </VTooltip>
          ) : null}
        </Flex>

        <Flex w={40} align="center" gap={2}>
          <CustomFieldTypeIcon field={field} size="md" />
          <span>{getCustomFieldTypeLabel(field)}</span>
        </Flex>

        <Box visibility={hasDotMenuItems ? 'visible' : 'hidden'}>
          <VDotMenu size="small" triggerLabel={`Options for ${field.label}`} items={dotMenuItems} />
        </Box>
      </Flex>
    </Flex>
  );
};

export default SettingsCustomFieldRow;
